import { action, computed } from "mobx";

import { FieldType, ViewModelBase } from "@shoothill/core";
import { AppUrls } from "AppUrls";
import { formatCurrencyFromPounds, formatDateTime } from "Utils/Format";
import { MyApprovalsActionedListItemModel } from "./MyApprovalsActionedListItemModel";
import { ApprovalListViewModel } from "./ApprovalListViewModel";

export class MyApprovalsActionedListItemViewModel extends ViewModelBase<MyApprovalsActionedListItemModel> {
    // #region Constructors and Disposers

    constructor() {
        super(new MyApprovalsActionedListItemModel());
    }

    // #endregion Constructors and Disposers

    // #region Properties

    @computed
    public get refNameFormatted(): string {
        if (this.model.reference && this.model.name) {
            return `${this.model.reference} - ${this.model.name}`;
        } else if (this.model.reference) {
            return `${this.model.reference}`;
        } else if (this.model.name) {
            return `${this.model.name}`;
        }

        return ``;
    }

    @computed
    public get requestedOnDate(): Date | null {
        return this.model.requestedDate;
    }

    @computed
    public get approvalSectionName(): string {
        return this.model.approvalSectionName;
    }

    @computed
    public get approvalTypeName(): string {
        return this.model.approvalTypeName;
    }

    @computed
    public get requestedBy(): string {
        return this.model.requestedBy;
    }

    @computed
    public get requisitionOrPOId(): string | null {
        return this.model.requisitionOrPOId;
    }

    @computed
    public get variationId(): string | null {
        return this.model.variationId;
    }

    @computed
    public get invoiceId(): string | null {
        return this.model.invoiceId;
    }

    @computed
    public get itemDescription() {
        return this.model.itemDescription;
    }

    @computed
    public get itemCost() {
        return this.model.itemCost;
    }

    @computed
    public get itemCostFormatted() {
        return this.model.itemCost ? formatCurrencyFromPounds(this.model.itemCost) : null;
    }

    @computed
    public get supplierName() {
        return this.model.supplierName;
    }

    // #endregion Properties

    // #region Actions

    @action
    public navigateToForm = (parentViewModel: ApprovalListViewModel): void => {
        // TODO: Update this to use an enum.
        switch (this.model.approvalSectionName) {
            case "Purchase Requisition":
                this.navigateToRequisition();
                break;
            case "Variation":
                this.navigateToVariation();
                break;
            case "Invoice":
                this.navigateToInvoice();
                break;
            case "Material":
                this.navigateToMaterialApproval(parentViewModel);
                break;
            case "Stock":
                this.navigateToStockApproval(parentViewModel);
                break;
            default:
                break;
        }
    };

    @action
    private navigateToRequisition = (): void => {
        this.history.push(AppUrls.Client.PurchaseOrder.Edit.replace(":poid", this.requisitionOrPOId!).replace(":approvalMode", "false"));
    };

    @action
    private navigateToVariation = (): void => {
        this.history.push(AppUrls.Client.Variation.Edit.replace(":variationid", this.variationId!).replace(":approvalMode", "false"));
    };

    @action
    private navigateToInvoice = (): void => {
        this.history.push(AppUrls.Client.Invoicing.Edit.replace(":invoiceid", this.invoiceId!).replace(":approvalMode", "false"));
    };

    // #endregion Actions

    // #region Approval View

    @action
    private navigateToMaterialApproval = (parentViewModel: ApprovalListViewModel): void => {
        parentViewModel.navigateToMaterialApproval(this);
    };

    @action
    private navigateToStockApproval = (parentViewModel: ApprovalListViewModel): void => {
        parentViewModel.navigateToStockApproval(this);
    };

    // #endregion Approval View

    // #region Viewmodel Boilerplate

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    public async isFieldValid(fieldName: keyof FieldType<MyApprovalsActionedListItemModel>): Promise<boolean> {
        return true;
    }

    // #endregion Viewmodel Boilerplate
}
